import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import Service from '../../../services';
import { baseUrlAdmin } from '../../../store/apiConstants';
import './styles.css';

class MarksEntry extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            saving: false,
            examination: null,
            students: [],
            originalStudents: [],
            allDeadlinesPassed: false,
            isCalculated: false,
            lastCalculatedAt: null,
            hasChanges: false,
            validationErrors: {}, // Track validation errors per student/subject
            sections: [], // Available sections
            selectedSectionId: null, // Selected section ID
            filteredStudents: [], // Students filtered by section
            selectedSubjectIds: [], // Selected subject IDs for display
            showInstructions: false, // Toggle instructions visibility
            noAccessMessage: null // Message when user has no access
        };
        this.service = new Service();
        this.toast = React.createRef();
    }

    componentDidMount() {
        if (this.props.visible && this.props.examId) {
            this.fetchMarksEntryData();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.visible && !prevProps.visible && this.props.examId) {
            this.fetchMarksEntryData();
        }
    }

    fetchMarksEntryData = async () => {
        try {
            this.setState({ loading: true });
            const response = await this.service.get(
                `${baseUrlAdmin}/subjective-examinations/marks-entry/${this.props.examId}`,
                true
            );

            if (response && response.res && response.res.success) {
                const { examination, students, allDeadlinesPassed, isCalculated, lastCalculatedAt } = response.res.data;

                // Handle case where no sections/subjects are assigned to user
                if (!examination || students.length === 0) {
                    const message = response.res.message || 'No sections or subjects assigned to you for this examination';
                    this.toast.current?.show({
                        severity: 'warn',
                        summary: 'No Access',
                        detail: message,
                        life: 5000
                    });
                    this.setState({
                        examination: null,
                        students: [],
                        originalStudents: [],
                        sections: [],
                        selectedSectionId: null,
                        filteredStudents: [],
                        selectedSubjectIds: [],
                        allDeadlinesPassed: false,
                        isCalculated: false,
                        lastCalculatedAt: null,
                        loading: false,
                        hasChanges: false,
                        validationErrors: {},
                        noAccessMessage: message
                    });
                    return;
                }

                // Extract unique sections from students
                const sectionsMap = new Map();
                students.forEach(student => {
                    if (student.sectionId && !sectionsMap.has(student.sectionId)) {
                        sectionsMap.set(student.sectionId, {
                            label: student.sectionName,
                            value: student.sectionId
                        });
                    }
                });
                const sections = Array.from(sectionsMap.values());

                // Select first section by default
                const selectedSectionId = sections.length > 0 ? sections[0].value : null;

                // Filter students by selected section
                const filteredStudents = selectedSectionId
                    ? students.filter(s => s.sectionId === selectedSectionId)
                    : students;

                // Sort students by enrollment ID
                // filteredStudents.sort((a, b) => {
                //     const enrollmentA = (a.enrollmentId || '').toLowerCase();
                //     const enrollmentB = (b.enrollmentId || '').toLowerCase();
                //     return enrollmentA.localeCompare(enrollmentB);
                // });

                // Select all subjects by default
                const selectedSubjectIds = examination.subjects.map(s => s.subjectId);

                this.setState({
                    examination,
                    students: JSON.parse(JSON.stringify(filteredStudents)), // Deep copy for editing
                    originalStudents: students, // Keep all students
                    sections,
                    selectedSectionId,
                    filteredStudents: JSON.parse(JSON.stringify(filteredStudents)),
                    selectedSubjectIds,
                    allDeadlinesPassed,
                    isCalculated,
                    lastCalculatedAt,
                    loading: false,
                    hasChanges: false,
                    validationErrors: {}, // Clear validation errors when loading new data
                    noAccessMessage: null
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error('Error fetching marks entry data:', error);
            this.toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch marks entry data',
                life: 3000
            });
            this.setState({ loading: false });
        }
    };

    handleSectionChange = (e) => {
        const selectedSectionId = e.value;
        const { originalStudents } = this.state;

        // Filter students by selected section
        const filteredStudents = originalStudents.filter(s => s.sectionId === selectedSectionId);

        // Sort students by enrollment ID
        // filteredStudents.sort((a, b) => {
        //     const enrollmentA = (a.enrollmentId || '').toLowerCase();
        //     const enrollmentB = (b.enrollmentId || '').toLowerCase();
        //     return enrollmentA.localeCompare(enrollmentB);
        // });

        this.setState({
            selectedSectionId,
            students: JSON.parse(JSON.stringify(filteredStudents)),
            filteredStudents: JSON.parse(JSON.stringify(filteredStudents)),
            hasChanges: false,
            validationErrors: {} // Clear validation errors when changing section
        });
    };

    handleMarksChange = (studentIndex, subjectIndex, value) => {
        const students = [...this.state.students];
        const subject = students[studentIndex].subjects[subjectIndex];
        const validationErrors = { ...this.state.validationErrors };
        const errorKey = `${studentIndex}-${subjectIndex}`;

        // Don't allow changes if subject is not assigned
        if (!subject.isAssigned) {
            return;
        }

        // Handle empty value
        if (value === '' || value === null) {
            subject.marksObtained = null;
            subject.isAbsent = false;
            delete validationErrors[errorKey];
        }
        // Handle "A" for absent
        else if (value.toUpperCase() === 'A') {
            subject.marksObtained = 'A';
            subject.isAbsent = true;
            delete validationErrors[errorKey];
        }
        // Handle numeric value
        else {
            const numericValue = parseFloat(value);

            // If invalid number, keep the original string value (not NaN) so user can correct it
            // But store null internally to avoid displaying "NaN"
            if (isNaN(numericValue)) {
                subject.marksObtained = null; // Don't store NaN
                subject.isAbsent = false;
                validationErrors[errorKey] = 'Invalid marks';
            } else {
                // Always update the value so user can see what they typed
                subject.marksObtained = numericValue;
                subject.isAbsent = false;

                // Validate numeric value
                if (numericValue < 0) {
                    validationErrors[errorKey] = 'Cannot be negative';
                } else if (numericValue > subject.maxMarks) {
                    validationErrors[errorKey] = `Max: ${subject.maxMarks}`;
                } else {
                    // Valid value - clear any existing error
                    delete validationErrors[errorKey];
                }
            }
        }

        this.setState({ students, validationErrors, hasChanges: true });
    };

    validateMarks = () => {
        const { validationErrors } = this.state;

        // Check if there are any validation errors
        const errorCount = Object.keys(validationErrors).length;

        if (errorCount > 0) {
            this.toast.current?.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Please fix ${errorCount} error(s) in the marks entry before saving`,
                life: 4000
            });
            return false;
        }

        return true;
    };

    handleSave = async () => {
        // Validate marks
        if (!this.validateMarks()) {
            return;
        }

        try {
            this.setState({ saving: true });

            // Detect changed marks by comparing current state with original state
            const changedMarks = [];

            // Create maps for quick lookup
            const originalStudentsMap = new Map(
                this.state.originalStudents.map(s => [s.studentId, s])
            );

            // Compare current students with original
            this.state.students.forEach(student => {
                const originalStudent = originalStudentsMap.get(student.studentId);
                if (!originalStudent) return;

                // Check each subject for changes
                student.subjects.forEach(subject => {
                    // Skip unassigned subjects
                    if (!subject.isAssigned) return;

                    // Find original subject
                    const originalSubject = originalStudent.subjects.find(s => s.subjectId === subject.subjectId);
                    if (!originalSubject) return;

                    // Check if marksObtained or isAbsent changed
                    const marksChanged = subject.marksObtained !== originalSubject.marksObtained;
                    const absentChanged = (subject.isAbsent || false) !== (originalSubject.isAbsent || false);

                    if (marksChanged || absentChanged) {
                        changedMarks.push({
                            studentId: student.studentId,
                            subjectId: subject.subjectId,
                            marksObtained: subject.marksObtained,
                            isAbsent: subject.isAbsent || false
                        });
                    }
                });
            });

            // If no changes detected, show message and return
            if (changedMarks.length === 0) {
                this.toast.current?.show({
                    severity: 'info',
                    summary: 'No Changes',
                    detail: 'No marks were changed',
                    life: 3000
                });
                this.setState({ saving: false });
                return;
            }

            // Send only changed marks to backend
            const response = await this.service.post(
                `${baseUrlAdmin}/subjective-examinations/marks-entry`,
                {
                    examId: this.props.examId,
                    changedMarks: changedMarks,
                    enteredBy: this.props.currentUser?.userId || null
                },
                true
            );

            if (response && response.res && response.res.success) {
                const updatedCount = response.res.updatedCount || changedMarks.length;
                this.toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Successfully updated ${updatedCount} mark(s)`,
                    life: 3000
                });

                // Update originalStudents to reflect saved changes
                this.setState({
                    originalStudents: JSON.parse(JSON.stringify(this.state.originalStudents.map(student => {
                        const editedStudent = this.state.students.find(s => s.studentId === student.studentId);
                        return editedStudent || student;
                    }))),
                    saving: false,
                    hasChanges: false,
                    validationErrors: {}
                });

                // Notify parent to refresh
                if (this.props.onSave) {
                    this.props.onSave();
                }

                // Close the dialog
                if (this.props.onHide) {
                    this.props.onHide();
                }
            } else {
                this.toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response?.message || 'Failed to save marks',
                    life: 3000
                });
                this.setState({ saving: false });
            }
        } catch (error) {
            console.error('Error saving marks:', error);
            this.toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to save marks',
                life: 3000
            });
            this.setState({ saving: false });
        }
    };

    renderMarksInput = (student, subject, studentIndex, subjectIndex) => {
        const { examination, validationErrors } = this.state;
        const errorKey = `${studentIndex}-${subjectIndex}`;
        const hasError = validationErrors[errorKey];

        // Check if deadline has passed for this specific subject
        const examSubject = examination.subjects.find(s => s.subjectId === subject.subjectId);
        const deadlinePassed = examSubject?.deadlinePassed || false;

        const isDisabled = !subject.isAssigned || deadlinePassed;

        // Display empty string for null, but show 0 if marks are actually entered as 0
        const displayValue = subject.marksObtained === null || subject.marksObtained === undefined
            ? ''
            : subject.marksObtained;

        return (
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <InputText
                    value={displayValue}
                    onChange={(e) => this.handleMarksChange(studentIndex, subjectIndex, e.target.value)}
                    disabled={isDisabled}
                    className={`marks-input ${!subject.isAssigned ? 'unassigned-subject' : ''} ${hasError ? 'p-invalid' : ''}`}
                    style={{
                        width: '80px',
                        textAlign: 'center',
                        backgroundColor: !subject.isAssigned ? '#f5f5f5' : 'white',
                        cursor: !subject.isAssigned ? 'not-allowed' : 'text',
                        borderColor: hasError ? '#e74c3c' : undefined,
                        borderWidth: hasError ? '2px' : undefined
                    }}
                    placeholder={!subject.isAssigned ? '-' : ''}
                    maxLength={5}
                    title={hasError ? validationErrors[errorKey] : ''}
                />
                {hasError && (
                    <small
                        style={{
                            color: '#e74c3c',
                            fontSize: '0.7rem',
                            position: 'absolute',
                            top: '-18px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            whiteSpace: 'nowrap',
                            fontWeight: '600',
                            backgroundColor: '#ffe6e6',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            zIndex: 1000,
                            border: '1px solid #e74c3c'
                        }}
                    >
                        {validationErrors[errorKey]}
                    </small>
                )}
            </div>
        );
    };

    renderSerialNumber = (rowData, options) => {
        return (
            <div style={{ textAlign: 'center', fontWeight: '600' }}>
                {options.rowIndex + 1}
            </div>
        );
    };

    renderStudentNameColumn = (rowData) => {
        return (
            <div style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}>
                <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                    {rowData.studentName}
                </div>
            </div>
        );
    };

    renderEnrollmentIdColumn = (rowData) => {
        return (
            <div style={{ textAlign: 'left', padding: '0.25rem 0.5rem' }}>
                {rowData.enrollmentId}
            </div>
        );
    };

    handleSubjectSelectionChange = (e) => {
        this.setState({ selectedSubjectIds: e.value });
    };

    render() {
        const { visible, onHide } = this.props;
        const { loading, saving, examination, students, allDeadlinesPassed, isCalculated, hasChanges, validationErrors, noAccessMessage } = this.state;

        const hasValidationErrors = Object.keys(validationErrors).length > 0;

        // Check how many subjects have passed deadlines
        const subjectsWithPassedDeadlines = examination?.subjects?.filter(s => s.deadlinePassed) || [];
        const someDeadlinesPassed = subjectsWithPassedDeadlines.length > 0;
        const allSubjectsDeadlinesPassed = subjectsWithPassedDeadlines.length === examination?.subjects?.length;

        const dialogFooter = (
            <div>
                {someDeadlinesPassed && (
                    <span style={{ color: '#e74c3c', marginRight: '1rem', fontSize: '0.9rem' }}>
                        <i className="pi pi-info-circle" style={{ marginRight: '0.25rem' }}></i>
                        {allSubjectsDeadlinesPassed
                            ? 'All subject deadlines have passed. Marks entry is disabled.'
                            : `${subjectsWithPassedDeadlines.length} subject(s) deadline passed: ${subjectsWithPassedDeadlines.map(s => s.subjectName).join(', ')}`
                        }
                    </span>
                )}
                {isCalculated && (
                    <span style={{ color: '#3498db', marginRight: '1rem', fontSize: '0.9rem' }}>
                        <i className="pi pi-check-circle" style={{ marginRight: '0.25rem' }}></i>
                        Results calculated
                    </span>
                )}
                {hasValidationErrors && (
                    <span style={{ color: '#e74c3c', marginRight: '1rem', fontSize: '0.9rem' }}>
                        <i className="pi pi-exclamation-triangle" style={{ marginRight: '0.25rem' }}></i>
                        Please fix {Object.keys(validationErrors).length} error(s)
                    </span>
                )}
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    onClick={onHide}
                    className="p-button-text"
                    disabled={saving}
                />
                {!noAccessMessage && (
                    <Button
                        label={saving ? 'Saving...' : 'Save Marks'}
                        icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                        onClick={this.handleSave}
                        disabled={loading || saving || allSubjectsDeadlinesPassed || !hasChanges || hasValidationErrors}
                    />
                )}
            </div>
        );

        return (
            <>
                <Toast ref={this.toast} />
                <Dialog
                    header={`Marks Entry - ${examination?.examName || ''}`}
                    visible={visible}
                    onHide={onHide}
                    style={{ width: '90vw', height: '90vh' }}
                    contentStyle={{
                        height: 'calc(100% - 60px)',
                        padding: 0,
                        overflow: 'hidden'
                    }}
                    modal
                    footer={dialogFooter}
                    maximizable
                    blockScroll
                >
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '3rem' }}>
                            <ProgressSpinner />
                            <p>Loading marks entry data...</p>
                        </div>
                    ) : (
                        <div className="marks-entry-container">
                            {/* Exam Info */}
                            <div className="exam-info" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                                    <div>
                                        <strong>Exam:</strong> {examination?.examName}
                                    </div>
                                    <div>
                                        <strong>Board:</strong> {examination?.boardName}
                                    </div>
                                    <div>
                                        <strong>Class:</strong> {examination?.className}
                                    </div>
                                </div>
                            </div>

                            {/* Filters Section */}
                            {this.state.sections.length > 0 && (
                                <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <label style={{ fontWeight: '600', minWidth: '60px' }}>
                                                Section:
                                            </label>
                                            <Dropdown
                                                value={this.state.selectedSectionId}
                                                options={this.state.sections}
                                                onChange={this.handleSectionChange}
                                                placeholder="Select a section"
                                                style={{ width: '200px' }}
                                            />
                                            <span style={{ fontSize: '0.85rem', color: '#666' }}>
                                                ({this.state.students.length} students)
                                            </span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                                            <label style={{ fontWeight: '600', minWidth: '60px' }}>
                                                Subjects:
                                            </label>
                                            <MultiSelect
                                                value={this.state.selectedSubjectIds}
                                                options={examination?.subjects?.map(s => ({ label: s.subjectName, value: s.subjectId })) || []}
                                                onChange={this.handleSubjectSelectionChange}
                                                placeholder="Select subjects to display"
                                                display="chip"
                                                style={{ width: '100%', maxWidth: '500px' }}
                                                maxSelectedLabels={10}
                                                selectedItemsLabel="{0} subjects selected"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Collapsible Instructions */}
                            <div style={{ marginBottom: '1rem', border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
                                <div
                                    style={{
                                        padding: '0.75rem 1rem',
                                        backgroundColor: '#e8f4f8',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        userSelect: 'none'
                                    }}
                                    onClick={() => this.setState({ showInstructions: !this.state.showInstructions })}
                                >
                                    <i className={`pi ${this.state.showInstructions ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{ fontSize: '0.85rem' }}></i>
                                    <strong style={{ fontSize: '0.9rem' }}>Instructions</strong>
                                </div>
                                {this.state.showInstructions && (
                                    <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f8f9fa', fontSize: '0.85rem' }}>
                                        <ul style={{ marginBottom: 0, marginTop: 0, paddingLeft: '1.5rem' }}>
                                            <li>Select a section from the dropdown above</li>
                                            <li>Select subjects to display (all selected by default)</li>
                                            <li>Enter marks for each subject (0 to max marks)</li>
                                            <li>Enter "A" for absent students</li>
                                            <li>Gray cells indicate subjects not assigned to the student</li>
                                            <li>Click "Save Marks" to save your entries</li>
                                            <li>Click "Recalculate" button (from main page) after saving to calculate grades and ranks</li>
                                        </ul>
                                    </div>
                                )}
                            </div>

                            {/* Marks Entry Table */}
                            <DataTable
                                value={students}
                                scrollable
                                scrollHeight="flex"
                                className="marks-entry-table"
                                emptyMessage="No students found"
                            >
                                <Column
                                    header="S.No"
                                    body={this.renderSerialNumber}
                                    frozen
                                    style={{ minWidth: '45px', maxWidth: '45px', width: '45px', textAlign: 'center', padding: '0.25rem' }}
                                />
                                <Column
                                    field="enrollmentId"
                                    header="Enrollment ID"
                                    body={this.renderEnrollmentIdColumn}
                                    frozen
                                    style={{ minWidth: '130px', padding: '0.25rem 0.5rem' }}
                                />
                                <Column
                                    field="studentName"
                                    header="Student Name"
                                    body={this.renderStudentNameColumn}
                                    frozen
                                    style={{ minWidth: '180px', padding: '0.25rem 0.5rem' }}
                                />

                                {examination?.subjects
                                    ?.filter(examSubject => this.state.selectedSubjectIds.includes(examSubject.subjectId))
                                    ?.map((examSubject, displayIndex) => {
                                        // Find the actual index in the full subjects array
                                        const actualSubjectIndex = examination.subjects.findIndex(s => s.subjectId === examSubject.subjectId);
                                        return (
                                            <Column
                                                key={examSubject.subjectId}
                                                header={
                                                    <div style={{ textAlign: 'center', padding: '0.25rem' }}>
                                                        <div style={{ fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}>
                                                            <span>{examSubject.subjectName}</span>
                                                            {examSubject.deadlineForMarksEntry && (
                                                                <i
                                                                    className="pi pi-info-circle"
                                                                    style={{
                                                                        fontSize: '0.85rem',
                                                                        color: examSubject.deadlinePassed ? '#e74c3c' : '#666',
                                                                        cursor: 'help'
                                                                    }}
                                                                    title={examSubject.deadlinePassed
                                                                        ? `Deadline passed: ${new Date(examSubject.deadlineForMarksEntry).toLocaleDateString()}`
                                                                        : `Deadline: ${new Date(examSubject.deadlineForMarksEntry).toLocaleDateString()}`
                                                                    }
                                                                ></i>
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '0.8rem',
                                                            fontWeight: '500',
                                                            color: '#0056D0',
                                                            backgroundColor: '#e3f2fd',
                                                            padding: '0.15rem 0.4rem',
                                                            borderRadius: '3px',
                                                            display: 'inline-block'
                                                        }}>
                                                            Max: {examSubject.maxMarks}
                                                        </div>
                                                        {examSubject.deadlinePassed && (
                                                            <div style={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: '500',
                                                                color: '#e74c3c',
                                                                backgroundColor: '#ffe6e6',
                                                                padding: '0.15rem 0.4rem',
                                                                borderRadius: '3px',
                                                                display: 'inline-block',
                                                                marginTop: '0.25rem'
                                                            }}>
                                                                <i className="pi pi-lock" style={{ fontSize: '0.7rem', marginRight: '0.25rem' }}></i>
                                                                Locked
                                                            </div>
                                                        )}
                                                    </div>
                                                }
                                                body={(rowData, options) => {
                                                    const student = students[options.rowIndex];
                                                    const subject = student.subjects[actualSubjectIndex];
                                                    return this.renderMarksInput(student, subject, options.rowIndex, actualSubjectIndex);
                                                }}
                                                style={{ minWidth: '100px', textAlign: 'center', padding: '0.25rem' }}
                                            />
                                        );
                                    })}
                            </DataTable>
                        </div>
                    )}
                </Dialog>
            </>
        );
    }
}

export default MarksEntry;
